/**
 * Local-job lifecycle. Replaces the backend job API.
 *
 * Flow:
 *   1. `createJob(videoFiles, audioFile, title)` writes 1 master audio +
 *      1..N video files to OPFS, records a fresh `LocalJob` in IndexedDB,
 *      and returns its id. Sync is launched immediately as a fire-and-
 *      forget task per video; UI should subscribe to `useOpsStore` for
 *      progress and to `jobEvents` for data-shape changes.
 *   2. `runQuickRender(jobId, opts)` produces an MP4 in OPFS using the
 *      sync result + user offset/drift overrides.
 *   3. `runEditRender(jobId, editSpec)` does the same for the full edit.
 *
 * Two channels:
 *   - In-memory `useOpsStore` carries transient progress (sync running,
 *     render running, last error). Reset on tab reload — the source of
 *     truth for "what's happening right now".
 *   - `jobEvents` is a global EventTarget that dispatches whenever the
 *     persisted job row changes (sync result lands, lastRender updates,
 *     etc). Subscribers re-read from IDB.
 */

import {
  isImageAsset,
  isVideoAsset,
  jobsDb,
  type JobMode,
  type LocalJob,
  type SyncResult,
  type VideoAsset,
} from "../storage/jobs-db";
import { useOpsStore } from "./ops-store";
import { nextCamIndex } from "./cam-ids";
import { defaultJobTitle } from "../lib/filenames";
import type { GradeParams } from "../core/fx/looks";
import { camColorAt } from "../storage/migrations";
import { opfs } from "../storage/opfs";
import {
  type AssetSource,
  type PickedAsset,
  loadAssetFile,
  persistPickedAsset,
  deleteAssetIfOwned,
} from "./asset-source";
import { syncAudio } from "./sync";
import { getOrComputeAnalysis } from "./render/audio-analysis";
import { getOrComputePyramid } from "./waveform/pyramid-cache";
import { detectChunks, pickGlobalBpm } from "./triage/chunk-detect";
import {
  DEFAULT_SILENCE_CONFIG,
} from "./triage/triage-orchestrator";
import { quickRender } from "./render/quick";
import {
  decodeStudioAudioInterleaved,
  type Segment,
} from "./render/edit";
import type {
  CamWorkerInput,
  EditWorkerEvent,
  EditWorkerInput,
  EditWorkerMessage,
  VisualizerWorkerDescriptor,
} from "./render/edit.worker";
import { decodeAudioToMonoPcm } from "./codec";
import { collectSyncFailureReport } from "./diagnostics";
import { computeEnergyCurves } from "./render/energy";
import { buildLoudnessEnvelope } from "../core/fx/audio-envelope";
import { extractTimelineFrames } from "./render/frames";
import type { TextOverlay } from "./render/ass-builder";
import {
  installRenderUnloadGuard,
  installSyncUnloadGuard,
  maybePromptQuotaPrune,
  removeRenderUnloadGuard,
  removeSyncUnloadGuard,
  requestPersistentStorage,
  sweepOrphanJobDirs,
} from "./lifecycle";
import { emitJobUpdate, jobEvents } from "./jobs-events";

export { jobEvents };

const AUDIO_NAME = "audio";
const OUTPUT_NAME = "output.mp4";

function audioPath(jobId: string, ext: string): string {
  return `jobs/${jobId}/${AUDIO_NAME}.${ext}`;
}

/** Resolve a job's master audio to a `File`. v3+ jobs carry an
 *  explicit `audioSource` (handle or OPFS); legacy v2 jobs derive the
 *  OPFS path from `audioFilename`'s extension. */
async function loadJobAudio(job: LocalJob, audioExt: string): Promise<File> {
  if (job.audioSource) {
    return loadAssetFile({ source: job.audioSource });
  }
  return opfs.readFile(audioPath(job.id, audioExt));
}

/** Pull the `AssetSource` off a v3+ asset row, or synthesise an
 *  `opfs`-kind source from the legacy `opfsPath` field for v2 rows. */
function assetSource(asset: { source?: AssetSource; opfsPath: string }): AssetSource {
  return asset.source ?? { kind: "opfs", path: asset.opfsPath };
}

/** Same for the master-audio side: prefer `job.audioSource`; fall back
 *  to the OPFS path derived from `audioFilename`. */
function jobAudioSource(job: LocalJob): AssetSource {
  if (job.audioSource) return job.audioSource;
  const ext = fileExtension(new File([], job.audioFilename), "wav");
  return { kind: "opfs", path: audioPath(job.id, ext) };
}
function outputPath(jobId: string): string {
  return `jobs/${jobId}/${OUTPUT_NAME}`;
}
function camVideoPath(jobId: string, camId: string, ext: string): string {
  return `jobs/${jobId}/${camId}.${ext}`;
}
function camFramesPath(jobId: string, camId: string): string {
  return `jobs/${jobId}/frames-${camId}.webp`;
}

function generateJobId(): string {
  // 12 hex chars from cryptographically random bytes; same density as a
  // UUID's prefix and avoids the dashes when used in URLs.
  const bytes = crypto.getRandomValues(new Uint8Array(6));
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function fileExtension(file: File, fallback: string): string {
  const dot = file.name.lastIndexOf(".");
  if (dot < 0 || dot === file.name.length - 1) return fallback;
  const ext = file.name.slice(dot + 1).toLowerCase();
  return /^[a-z0-9]{1,8}$/i.test(ext) ? ext : fallback;
}

// -----------------------------------------------------------------------------
// Single source of truth for "persist a media file as a new cam slot in OPFS
// + return the matching asset record". Used by both the upfront upload flow
// (createJob — loops over the user's videos) and the per-cam Editor "+ Media"
// flow (addVideoToJob / addImageToJob — one cam per call).
// -----------------------------------------------------------------------------

/** Persist a video pick (handle-backed or file-only) as cam-{index+1}
 *  and return the VideoAsset record. For handle-backed picks no bytes
 *  are copied — we keep the FileSystemFileHandle in the asset's
 *  `source`. For file-only picks (legacy / unsupported browsers) the
 *  bytes are written to OPFS and `source` points there. The
 *  `opfsPath` field is always set to the canonical per-cam path so
 *  derived paths (frames-strip etc.) stay deterministic. */
async function persistVideoCam(
  jobId: string,
  picked: PickedAsset,
  index: number,
): Promise<VideoAsset> {
  const camId = `cam-${index + 1}`;
  const ext = fileExtension(picked.file, "mp4");
  const opfsPath = camVideoPath(jobId, camId, ext);
  const source = await persistPickedAsset(picked, opfsPath);
  return {
    id: camId,
    filename: picked.file.name,
    opfsPath,
    source,
    color: camColorAt(index),
  };
}

/** Persist an image pick — same dual-path logic as `persistVideoCam`,
 *  plus a best-effort dimension probe. */
async function persistImageCam(
  jobId: string,
  picked: PickedAsset,
  index: number,
  durationS: number,
): Promise<{
  kind: "image";
  id: string;
  filename: string;
  opfsPath: string;
  source: AssetSource;
  color: string;
  durationS: number;
  width?: number;
  height?: number;
}> {
  const camId = `cam-${index + 1}`;
  const ext = fileExtension(picked.file, "png");
  const opfsPath = camVideoPath(jobId, camId, ext);
  const source = await persistPickedAsset(picked, opfsPath);
  let width: number | undefined;
  let height: number | undefined;
  try {
    const bitmap = await createImageBitmap(picked.file);
    width = bitmap.width;
    height = bitmap.height;
    bitmap.close();
  } catch {
    // ignore — non-fatal
  }
  return {
    kind: "image",
    id: camId,
    filename: picked.file.name,
    opfsPath,
    source,
    color: camColorAt(index),
    durationS,
    width,
    height,
  };
}

interface CreateJobOptions {
  /** Optional title; defaults to the master audio's name (the song is
   *  the project's identity), then the first video's name. */
  title?: string | null;
  /** Workflow-Pfad. Default `"direct"` für die Legacy-Sync-Flow.
   *  `"longform"` schaltet den Triage → Arrange → Editor-Pfad frei. */
  mode?: JobMode;
}

/**
 * Register a new job and kick off sync. Each pick is either:
 *   - handle-backed (`{file, handle}`) — we keep the handle and read
 *     bytes from the user's disk on demand (no copy)
 *   - file-only (`{file}`) — we copy the bytes into OPFS as a fallback
 *     for browsers without `showOpenFilePicker` (Safari, Firefox)
 *
 * Returns immediately with the new job id; subscribe to `jobEvents`
 * for status updates.
 *
 * `videoPicks` must contain at least one video; the first one becomes
 * cam-1 (the lane that's mirrored into legacy top-level fields for
 * backward compat).
 */
export async function createJob(
  videoPicks: PickedAsset[],
  audioPick: PickedAsset,
  options: CreateJobOptions = {},
): Promise<string> {
  if (videoPicks.length === 0) {
    throw new Error("At least one video is required");
  }

  // Best-effort housekeeping before we commit big new files: ask for
  // persistent storage (so the browser doesn't evict OPFS under pressure),
  // reclaim orphaned OPFS dirs from failed past uploads (#119), and — when
  // storage is tight — offer to delete old finished projects. The user
  // decides; nothing is pruned without consent (#64).
  void requestPersistentStorage();
  await sweepOrphanJobDirs().catch(() => undefined);
  await maybePromptQuotaPrune().catch(() => undefined);

  const jobId = generateJobId();
  const audioExt = fileExtension(audioPick.file, "wav");
  const audioOpfsPath = audioPath(jobId, audioExt);

  // Persist all picks before the job row exists. If any copy fails
  // (QuotaExceededError on Safari/Firefox where picks are byte copies),
  // remove everything written so far — otherwise the orphaned bytes are
  // invisible to History/delete/prune and make the quota problem that
  // caused the failure permanently worse (#119).
  let audioSource: AssetSource;
  const videos: VideoAsset[] = [];
  try {
    audioSource = await persistPickedAsset(audioPick, audioOpfsPath);
    for (let i = 0; i < videoPicks.length; i++) {
      videos.push(await persistVideoCam(jobId, videoPicks[i], i));
    }
  } catch (err) {
    await opfs.deletePath(`jobs/${jobId}`).catch(() => undefined);
    throw err;
  }

  const firstVideo = videoPicks[0].file;

  const job: LocalJob = {
    id: jobId,
    title: options.title ?? defaultJobTitle(audioPick.file.name, firstVideo.name),
    // Legacy V1 mirrors of cam-1 (kept for backward compat — older callers
    // may still read videoFilename / sync / dimensions at the top level).
    videoFilename: firstVideo.name,
    audioFilename: audioPick.file.name,
    audioSource,
    createdAt: Date.now(),
    schemaVersion: 3,
    videos,
    cuts: [],
    mode: options.mode ?? "direct",
  };
  await jobsDb.saveJob(job);
  emitJobUpdate(job);

  // Kick off sync without awaiting — UI subscribes for results.
  startSyncRun(jobId, audioExt);

  return jobId;
}

/**
 * Fire-and-forget sync launcher shared by `createJob` and `retrySync`:
 * registers the op in the store, guards against accidental tab-close
 * while the multi-minute pipeline runs (#65), and converts a failure
 * into a rich diagnostic report on the op.
 */
function startSyncRun(jobId: string, audioExt: string): void {
  useOpsStore.getState().startSyncOp(jobId, { pct: 0, stage: "queued" });
  installSyncUnloadGuard(jobId);
  void runSync(jobId, audioExt)
    .catch(async (err) => {
      // Build a rich diagnostic report so a screenshot of the banner is
      // self-sufficient for triage — browser, capabilities, file in
      // flight, and the original Error.name/stack survive instead of
      // being flattened into a single opaque "File could not be read!"
      // string. Banner shows the summary; full report ships behind a
      // "Show details" toggle on the same banner.
      const { summary, report } = await collectSyncFailureReport(jobId, err);
      useOpsStore.getState().failSyncOp(jobId, summary, report);
    })
    .finally(() => {
      removeSyncUnloadGuard(jobId);
    });
}

/**
 * Re-run sync for an existing job from its persisted assets (#65).
 *
 * Covers the two dead ends the ops-store design leaves behind: a reload
 * mid-sync (progress lives only in memory → the row sits at "needs
 * sync" forever) and a failed run. Cams that already carry a completed
 * prep (sync result or frame strip) are kept as-is — see the resume
 * logic in `runSync` — so a retry only redoes the missing work.
 *
 * No-op when a sync for this job is already running.
 */
export async function retrySync(jobId: string): Promise<void> {
  const job = await jobsDb.getJob(jobId);
  if (!job) throw new Error(`Job not found: ${jobId}`);
  const running = useOpsStore.getState().ops[jobId]?.sync;
  if (running && !running.error) return;
  const audioExt = fileExtension(new File([], job.audioFilename), "wav");
  startSyncRun(jobId, audioExt);
}

interface SyncProgressPatch {
  pct?: number;
  stage?: string;
  detail?: string;
  currentFile?: import("./ops-store").SyncOpFileContext;
}

function reportSyncProgress(jobId: string, patch: SyncProgressPatch): void {
  useOpsStore.getState().updateSyncOp(jobId, patch);
}

interface RenderProgressPatch {
  pct?: number;
  stage?: string;
  framesDone?: number;
  framesTotal?: number;
}

function reportRenderProgress(jobId: string, patch: RenderProgressPatch): void {
  useOpsStore.getState().updateRenderOp(jobId, patch);
}

/**
 * Per-cam preparation: decode + match (unless skipped) + probe + frames.
 *
 * Decoupled from any specific orchestration so it can be reused by the
 * upfront `runSync` (batches all cams) and by `addVideoToJob` (one cam
 * added later from the editor).
 *
 * `mapPct` lets the caller control how the per-cam fractional progress
 * (0..1) is mapped onto the job's global pct bar — runSync uses a per-cam
 * band of the 5..95% range; addVideoToJob uses the cam's nominal band so
 * the SyncProgressPanel keeps showing it correctly.
 *
 * `studioPcm` is required when matching; pass `null` when `skipSync` is
 * true to indicate the master audio doesn't need decoding.
 *
 * Frame extraction always runs — the timeline lane needs thumbnails even
 * for B-roll cams the user opted out of matching.
 */
async function runCamPrep(
  jobId: string,
  cam: VideoAsset,
  studioPcm: Float32Array | null,
  opts: {
    skipSync?: boolean;
    mapPct: (frac: number) => number;
  },
): Promise<VideoAsset> {
  // Record the file we're about to read BEFORE the load — if loading
  // itself throws (OPFS read failure, handle permission revoked) the
  // diagnostic still names which cam was in flight. We refine with the
  // resolved File's actual size/type once the load succeeds.
  reportSyncProgress(jobId, {
    currentFile: {
      name: cam.filename,
      sourceKind: assetSource(cam).kind,
    },
  });
  const videoFile = await loadAssetFile(cam);
  reportSyncProgress(jobId, {
    currentFile: {
      name: videoFile.name,
      size: videoFile.size,
      type: videoFile.type,
      sourceKind: assetSource(cam).kind,
    },
  });

  // SYNC stage — only if matching is requested.
  let sync: SyncResult | undefined;
  if (!opts.skipSync) {
    if (!studioPcm) {
      throw new Error(`runCamPrep: studioPcm is required when skipSync is false`);
    }
    reportSyncProgress(jobId, {
      pct: opts.mapPct(0),
      stage: `syncing-${cam.id}`,
      detail: `${cam.id} · ${cam.filename}`,
    });

    // Decode is the slowest piece of the per-cam band — for a 9 GB
    // recording it eats ~10 s. Stream the streaming-decoder's
    // bytes-read fraction into the global progress bar so the user
    // sees continuous motion instead of a hard 0 → 0.4 jump.
    const videoMonoPcm = await decodeAudioToMonoPcm(videoFile, 22050, {
      onProgress: (frac) => {
        reportSyncProgress(jobId, {
          pct: opts.mapPct(frac * 0.4),
          stage: `syncing-${cam.id}`,
          detail: `${cam.id} · ${cam.filename}`,
        });
      },
    });
    reportSyncProgress(jobId, {
      pct: opts.mapPct(0.4),
      stage: `syncing-${cam.id}`,
      detail: `${cam.id} · ${cam.filename}`,
    });

    // Sync gets the 0.4 → 0.55 slice of the per-cam progress band. The
    // WASM pipeline calls back per stage (envelope→chroma→onset→ncc→
    // phat→consensus→drift), so the bar moves continuously through the
    // ~5-s match instead of freezing at 40 %.
    const result = await syncAudio(
      {
        refSource: videoMonoPcm.pcm,
        querySource: studioPcm,
      },
      {
        onProgress: (stage, frac) => {
          reportSyncProgress(jobId, {
            pct: opts.mapPct(0.4 + frac * 0.15),
            stage: `syncing-${cam.id}`,
            detail: `${cam.id} · ${cam.filename} · ${stage}`,
          });
        },
      },
    );
    sync = {
      offsetMs: result.offsetMs,
      driftRatio: result.driftRatio,
      confidence: result.confidence,
      warning: result.warning ?? undefined,
      candidates: result.candidates,
    };
  }

  // Probe video for duration / dimensions. Best-effort; missing fields
  // surface as undefined and the editor falls back to defaults.
  let durationS: number | undefined;
  let width: number | undefined;
  let height: number | undefined;
  let intrinsicRotationDeg: 0 | 90 | 180 | 270 | undefined;
  try {
    const { demuxVideoTrack } = await import("./codec/webcodecs/demux");
    const v = await demuxVideoTrack(videoFile);
    if (v) {
      durationS = v.info.durationS;
      width = v.info.width;
      height = v.info.height;
      intrinsicRotationDeg = v.info.rotationDeg;
    }
  } catch {
    // ignore — nice-to-have
  }

  // Per-cam thumbnail strip. Always runs (B-roll lanes need thumbnails too).
  // Failure is non-blocking.
  let framesPath: string | undefined;
  let framesOrientation: "codec" | "display" | undefined;
  reportSyncProgress(jobId, {
    pct: opts.mapPct(0.6),
    stage: `frames-${cam.id}`,
    detail: `${cam.id} · ${cam.filename}`,
  });
  try {
    // Bake the source's display rotation into the strip so every
    // downstream consumer sees upright tiles without re-applying the
    // rotation themselves (was a per-consumer bug-magnet).
    const r = await extractTimelineFrames(videoFile, {
      rotationDeg: intrinsicRotationDeg,
      onProgress: (frac) => {
        reportSyncProgress(jobId, {
          pct: opts.mapPct(0.6 + frac * 0.4),
          stage: `frames-${cam.id}`,
          detail: `${cam.id} · ${cam.filename}`,
        });
      },
    });
    framesPath = camFramesPath(jobId, cam.id);
    framesOrientation = "display";
    await opfs.writeFile(framesPath, r.blob);
  } catch (err) {
    console.warn(`Frame strip extraction failed for ${jobId}/${cam.id}:`, err);
  }

  return {
    ...cam,
    sync,
    durationS,
    width,
    height,
    intrinsicRotationDeg,
    framesPath,
    framesOrientation,
  };
}

async function runSync(jobId: string, audioExt: string): Promise<void> {
  reportSyncProgress(jobId, { pct: 2, stage: "loading" });

  const job = await jobsDb.getJob(jobId);
  if (!job?.videos?.length) {
    throw new Error(`Job ${jobId} has no videos to sync`);
  }
  // runSync only runs at upload time, when videos[] is exclusively
  // VideoAssets (createJob never appends images). Filter defensively in
  // case a future caller sneaks images in — they don't get prep here.
  const videos = job.videos.filter(isVideoAsset);
  if (videos.length === 0) {
    throw new Error(`Job ${jobId}: no video cams to sync`);
  }

  // Decode the master studio audio once; every cam syncs against this.
  // Master decode gets the 2..5 % slice of the global bar — small for
  // typical few-MB songs but visible for hour-long master tracks.
  const audioSrc = jobAudioSource(job);
  reportSyncProgress(jobId, {
    currentFile: { name: job.audioFilename, sourceKind: audioSrc.kind },
  });
  const audioFile = await loadJobAudio(job, audioExt);
  reportSyncProgress(jobId, {
    pct: 2,
    stage: "decoding-studio-audio",
    currentFile: {
      name: audioFile.name,
      size: audioFile.size,
      type: audioFile.type,
      sourceKind: audioSrc.kind,
    },
  });
  const studioMonoPcm = await decodeAudioToMonoPcm(audioFile, 22050, {
    onProgress: (frac) => {
      reportSyncProgress(jobId, {
        pct: 2 + Math.floor(frac * 3),
        stage: "decoding-studio-audio",
      });
    },
  });

  const updatedVideos: VideoAsset[] = [];
  // Each cam gets an equal slice of the 5-95% progress band.
  const bandPerCam = 90 / videos.length;

  for (let i = 0; i < videos.length; i++) {
    const cam = videos[i];
    const camStartPct = 5 + i * bandPerCam;
    // Resume support (#65): a cam whose prep already completed in an
    // earlier (partially-finished) run keeps its result instead of
    // redoing minutes of decode+match work. Either marker counts:
    // `sync` (matched cam) or `framesPath` (written after sync — also
    // covers deliberately-unmatched B-roll cams).
    if (cam.sync || cam.framesPath) {
      updatedVideos.push(cam);
      continue;
    }
    const updated = await runCamPrep(jobId, cam, studioMonoPcm.pcm, {
      mapPct: (frac) =>
        Math.min(95, Math.floor(camStartPct + frac * bandPerCam)),
    });
    updatedVideos.push(updated);

    // Persist this cam's result immediately (#65): a failure on a later
    // cam — or a reload mid-sync — must not throw away the completed
    // decode+match+frame-strip work of the cams before it.
    try {
      const cur = await jobsDb.getJob(jobId);
      if (cur) {
        const merged = (cur.videos ?? []).map((v) =>
          v.id === updated.id ? updated : v,
        );
        emitJobUpdate(await jobsDb.updateJob(jobId, { videos: merged }));
      }
    } catch (err) {
      // Non-fatal: the final batch write below still covers this cam.
      console.warn(`Incremental sync persist failed for ${jobId}/${cam.id}:`, err);
    }
  }

  // Mirror cam-1's stats to the legacy top-level fields so consumers that
  // haven't moved to videos[] yet still see something sensible.
  const lead = updatedVideos[0];
  const synced = await jobsDb.updateJob(jobId, {
    videos: updatedVideos,
    sync: lead.sync,
    durationS: lead.durationS,
    width: lead.width,
    height: lead.height,
    hasFrames: !!lead.framesPath,
  });
  emitJobUpdate(synced);
  reportSyncProgress(jobId, { pct: 95, stage: "analyzing-audio" });

  // Audio-Analyse-Phase: Spectral-Flux Onsets, Tempo, Beats. Cached per job
  // in IDB so the editor can read it without recomputing. Failure is
  // non-blocking — the sync result is the critical artifact, the analysis
  // is value-add.
  try {
    await getOrComputeAnalysis(jobId, studioMonoPcm.pcm, studioMonoPcm.sampleRate);
  } catch (err) {
    console.warn(`Audio analysis failed for ${jobId} (non-fatal):`, err);
  }

  // Waveform peak-pyramid: precompute + persist here, where the master PCM is
  // already decoded, so Triage shows the high-res waveform instantly instead of
  // rebuilding it on every open. Non-blocking — jobs synced before this existed
  // fall back to the lazy in-memory build in the timeline.
  try {
    await getOrComputePyramid(
      jobId,
      studioMonoPcm.pcm,
      studioMonoPcm.sampleRate,
    );
  } catch (err) {
    console.warn(`Waveform pyramid build failed for ${jobId} (non-fatal):`, err);
  }

  // Long-form chunk detection — only for `mode: "longform"` jobs.
  // Runs envelope + silence-segments and persists the result so the
  // Triage screen can open instantly without any "decoding…" wait.
  // BPM is song-global and produced by the audio-analysis step above,
  // so no per-chunk tempo work happens here. Direct-mode jobs skip
  // this entirely.
  const jobForMode = await jobsDb.getJob(jobId);
  if (jobForMode?.mode === "longform") {
    try {
      reportSyncProgress(jobId, { pct: 97, stage: "detecting-chunks" });
      const detection = await detectChunks(
        studioMonoPcm.pcm,
        studioMonoPcm.sampleRate,
        DEFAULT_SILENCE_CONFIG,
      );
      // Aggregate per-chunk BPMs into the song-global tempo. Whole-file
      // BPM detection is unreliable on long-form session recordings —
      // each chunk votes with its own duration as weight, the most-
      // common BPM wins. The detected number lands in `job.bpm`
      // (manualOverride: false) so the editor + triage UI both see it.
      // If the audio-analysis step above already set a manual BPM the
      // user shouldn't get overridden — only fill in when nothing's
      // there yet or when the existing value is auto-detected.
      const fresh = await jobsDb.getJob(jobId);
      const existingBpm = fresh?.bpm;
      const updates: Partial<LocalJob> = {
        chunks: detection.chunks,
        silenceConfig: DEFAULT_SILENCE_CONFIG,
        triageEnvelope: detection.envelope,
      };
      if (!existingBpm?.manualOverride) {
        const globalBpm = pickGlobalBpm(detection.chunks);
        if (globalBpm) {
          updates.bpm = {
            value: globalBpm.value,
            confidence: globalBpm.confidence,
            phase: existingBpm?.phase ?? 0,
            manualOverride: false,
          };
        }
      }
      const after = await jobsDb.updateJob(jobId, updates);
      emitJobUpdate(after);
    } catch (err) {
      console.warn(`Chunk detection failed for ${jobId} (non-fatal):`, err);
    }
  }

  // Done — clear the sync op. The persisted data on the job row is the
  // signal "this project is past sync" (videos[].sync filled, optionally
  // chunks/triageEnvelope for longform).
  useOpsStore.getState().clearSyncOp(jobId);
}

// -----------------------------------------------------------------------------
// addVideoToJob — append a cam to an existing project (editor "+ Media")
// -----------------------------------------------------------------------------

export interface AddVideoOptions {
  /** Skip the audio-match stage. The cam still gets dimensions probed and
   *  thumbnails extracted, but `sync` and `candidates` stay undefined.
   *  Used for B-roll the user wants to place by hand. */
  skipSync?: boolean;
}

/**
 * Append a video to an existing job and run the per-cam preparation in the
 * background. Returns the new cam id immediately so callers can update UI
 * (e.g. spin up an empty lane). The cam is persisted with `sync` and
 * `framesPath` undefined; both fill in via `jobsDb.updateJob` events as
 * the prep progresses.
 *
 * Errors during prep are logged but don't fail the job — the new cam stays
 * in videos[] without sync/frames so the user can retry or remove it.
 */
export async function addVideoToJob(
  jobId: string,
  picked: PickedAsset,
  opts: AddVideoOptions = {},
): Promise<string> {
  const job = await jobsDb.getJob(jobId);
  if (!job) throw new Error(`Job ${jobId} not found`);

  const existing = job.videos ?? [];
  // Collision-free slot: removeCamFromJob keeps survivor ids stable (no
  // renumbering), so `existing.length` would re-issue a live id after a
  // delete-then-add and corrupt that cam's lane + OPFS media (#68).
  const newCam = await persistVideoCam(jobId, picked, nextCamIndex(existing));
  const camId = newCam.id;

  // Probe dimensions BEFORE the first persist so the editor lane appears
  // with its real duration immediately — without this the lane would be
  // a zero-width invisible sliver until runCamPrep finishes seconds later.
  try {
    const { demuxVideoTrack } = await import("./codec/webcodecs/demux");
    const v = await demuxVideoTrack(picked.file);
    if (v) {
      newCam.durationS = v.info.durationS;
      newCam.width = v.info.width;
      newCam.height = v.info.height;
    }
  } catch {
    // ignore — runCamPrep will probe again as a fallback
  }

  // Append immediately (sync still undefined) so editor lane appears now.
  const next = [...existing, newCam];
  const saved = await jobsDb.updateJob(jobId, { videos: next });
  emitJobUpdate(saved);

  // Run cam-prep in the background. Don't block the caller — the editor
  // subscribes to jobEvents to know when the cam is ready.
  void (async () => {
    try {
      // Studio PCM only needed when actually matching.
      let studioPcm: Float32Array | null = null;
      if (!opts.skipSync) {
        const audioExt = fileExtension(
          { name: job.audioFilename } as File,
          "wav",
        );
        const audioFile = await loadJobAudio(job, audioExt);
        const decoded = await decodeAudioToMonoPcm(audioFile, 22050);
        studioPcm = decoded.pcm;
      }

      // Project this cam onto the multi-cam progress panel: it sits at the
      // very end of videos[] now, so its band mirrors what runSync would
      // have used for an N-cam batch.
      const totalCams = next.length;
      const bandPerCam = 90 / totalCams;
      const camStartPct = 5 + (totalCams - 1) * bandPerCam;

      const prepared = await runCamPrep(jobId, newCam, studioPcm, {
        skipSync: opts.skipSync,
        mapPct: (frac) =>
          Math.min(95, Math.floor(camStartPct + frac * bandPerCam)),
      });

      // Re-read videos[] before writing — user may have added more cams in
      // the meantime, or the editor may have edited per-cam state.
      const cur = await jobsDb.getJob(jobId);
      if (!cur) return;
      const updated = (cur.videos ?? []).map((v) =>
        v.id === camId ? { ...v, ...prepared } : v,
      );
      const final = await jobsDb.updateJob(jobId, { videos: updated });
      emitJobUpdate(final);
    } catch (err) {
      console.error(
        `addVideoToJob: cam-prep failed for ${jobId}/${camId}:`,
        err,
      );
      // The cam stays in videos[] without sync/frames; UI surface it as
      // "not synced" — user can remove or retry.
    }
  })();

  return camId;
}

// -----------------------------------------------------------------------------
// addImageToJob — append a still-image clip to an existing project
// -----------------------------------------------------------------------------

export interface AddImageOptions {
  /** Initial duration on the master timeline (seconds). User can resize
   *  via the lane handle later. */
  durationS?: number;
}

const DEFAULT_IMAGE_DURATION_S = 5;

/**
 * Append an image asset to an existing job. Images don't have audio, sync
 * candidates, or thumbnail strips — only a user-set duration and a free
 * placement offset. Probing dimensions runs synchronously here (it's
 * cheap for an image: decode → naturalWidth/Height) so the lane gets
 * its aspect ratio without a background pass.
 */
export async function addImageToJob(
  jobId: string,
  picked: PickedAsset,
  opts: AddImageOptions = {},
): Promise<string> {
  const job = await jobsDb.getJob(jobId);
  if (!job) throw new Error(`Job ${jobId} not found`);

  const existing = job.videos ?? [];
  const durationS = opts.durationS ?? DEFAULT_IMAGE_DURATION_S;
  // Same collision-free slot rule as addVideoToJob (#68).
  const newAsset = await persistImageCam(
    jobId,
    picked,
    nextCamIndex(existing),
    durationS,
  );

  const next = [...existing, newAsset];
  const saved = await jobsDb.updateJob(jobId, { videos: next });
  emitJobUpdate(saved);

  return newAsset.id;
}

interface QuickRenderInput {
  /** User offset override added on top of the algorithm's offsetMs. */
  offsetOverrideMs?: number;
}

export async function runQuickRender(
  jobId: string,
  opts: QuickRenderInput = {},
): Promise<void> {
  const job = await jobsDb.getJob(jobId);
  if (!job) throw new Error(`Job not found: ${jobId}`);
  if (!job.sync) throw new Error("Cannot render before sync completes.");

  installRenderUnloadGuard(jobId);
  useOpsStore.getState().startRenderOp(jobId, { pct: 5, stage: "render-prep" });
  try {
    // V1 limitation (same as runEditRender): renders cam-1 only.
    const cam1 = job.videos?.[0];
    if (!cam1 || isImageAsset(cam1)) {
      throw new Error("No video found for this job.");
    }
    const audioExt = fileExtension(new File([], job.audioFilename), "wav");
    const videoFile = await loadAssetFile(cam1);
    const audioFile = await loadJobAudio(job, audioExt);

    reportRenderProgress(jobId, { pct: 15, stage: "encoding" });
    const totalOffsetMs = job.sync.offsetMs + (opts.offsetOverrideMs ?? 0);
    const result = await quickRender({
      videoFile,
      audioFile,
      offsetMs: totalOffsetMs,
      driftRatio: job.sync.driftRatio,
    });

    reportRenderProgress(jobId, { pct: 90, stage: "writing" });
    await opfs.writeFile(outputPath(jobId), result.output);

    const updated = await jobsDb.updateJob(jobId, {
      lastRender: {
        completedAt: Date.now(),
        outputBytes: result.output.byteLength,
      },
    });
    emitJobUpdate(updated);
    useOpsStore.getState().finishRenderOp(jobId);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    useOpsStore.getState().failRenderOp(jobId, message);
    throw err;
  } finally {
    removeRenderUnloadGuard(jobId);
  }
}

export interface EditSpecLocal {
  segments: Segment[];
  overlays: TextOverlay[];
  offsetOverrideMs?: number;
  /**
   * Subset of visualizers to add — built lazily inside runEditRender from
   * the audio PCM + energy curves. The UI sends a small descriptor; we
   * expand it here.
   */
  visualizers?: VisualizerDescriptor[];
  /** Resolved encoder/output options. Built by the editor's submit handler
   *  from the user's `ExportSpec` (UI layer) — this stays raw kbps/codec so
   *  the worker doesn't need to know about presets. */
  exportOpts?: ExportRenderOpts;
  /** Output filename (without extension). Used purely for download UX. */
  outputFilename?: string;
  /** Per-cam render-time overrides built from the editor's clips slice. */
  clipOverrides?: Array<{
    id: string;
    syncOverrideMs: number;
    startOffsetS: number;
    rotation?: number;
    flipX?: boolean;
    flipY?: boolean;
    viewportTransform?: import("../core/types").ViewportTransform;
  }>;
  /** Master-audio playback gain. 1.0 = source level. Picked up at render
   *  start from the store's `audioVolume`. Optional / undefined → 1.0. */
  audioVolume?: number;
  /** Multi-cam cuts — drives the multi-source frame loop. */
  cuts?: Array<{ atTimeS: number; camId: string }>;
  /** Long-form arrangement-mode pills. Each pill anchors a cam-source
   *  excerpt to a position on the song timeline; the renderer composes
   *  video by walking pills in arr-time + applying cuts on top. Empty
   *  in direct-mode, in which case the renderer falls back to the
   *  legacy per-cam contiguous-range model. */
  pills?: import("../core/types").Pill[];
}

export interface ExportRenderOpts {
  width?: number;
  height?: number;
  videoCodec: "h264" | "h265";
  audioCodec: "aac" | "opus";
  videoBitrateBps: number;
  audioBitrateBps: number;
}

export type VisualizerDescriptor =
  | { type: "showwaves" }
  | { type: "showfreqs" };

/**
 * Tracks renders that are currently running so cancelEditRender can
 * terminate the underlying worker. One entry per active job; renders
 * for different jobs run concurrently with their own workers.
 */
const activeRenders = new Map<string, Worker>();

/**
 * Start the edit-render. Returns a promise that resolves when the worker
 * reports `done` or rejects on `error`. The caller is free to ignore the
 * promise — the function emits jobEvents along the way, so the UI can
 * subscribe through that channel instead.
 *
 * Cancel by calling `cancelEditRender(jobId)`.
 */
export async function runEditRender(
  jobId: string,
  spec: EditSpecLocal,
): Promise<void> {
  const job = await jobsDb.getJob(jobId);
  if (!job) throw new Error(`Job not found: ${jobId}`);
  if (!job.sync) throw new Error("Cannot render before sync completes.");
  if (activeRenders.has(jobId)) {
    throw new Error("Render already in progress for this job.");
  }

  installRenderUnloadGuard(jobId);
  useOpsStore.getState().startRenderOp(jobId, { pct: 5, stage: "render-prep" });
  let workerOwned: Worker | null = null;
  try {
    const videos = job.videos ?? [];
    if (videos.length === 0) throw new Error("No video found for this job.");
    const audioExt = fileExtension(new File([], job.audioFilename), "wav");
    const audioFile = await loadJobAudio(job, audioExt);

    // Audio decode + energy curves run on the main thread because
    // AudioContext.decodeAudioData isn't available in workers. Both are
    // fast (a few seconds for a 3-min file) and the produced Float32Arrays
    // are transferred zero-copy into the worker.
    reportRenderProgress(jobId, { pct: 10, stage: "audio-decode" });
    const audio = await decodeStudioAudioInterleaved(audioFile);
    // Bake the master-audio volume into the PCM in-place. Skipped when
    // unity (1.0 / undefined) so the common path stays a pure decode.
    const audioVolumeRaw = spec.audioVolume;
    const audioVolume =
      typeof audioVolumeRaw === "number" && audioVolumeRaw >= 0
        ? Math.min(4, audioVolumeRaw)
        : 1;
    if (audioVolume !== 1) {
      const pcm = audio.pcm;
      for (let i = 0; i < pcm.length; i++) {
        pcm[i] *= audioVolume;
      }
    }

    let monoPcm: Float32Array | null = null;
    let energy: ReturnType<typeof computeEnergyCurves> | null = null;
    const needsPcm =
      (spec.visualizers && spec.visualizers.length > 0) ||
      spec.overlays.some((o) => o.reactiveBand);
    if (needsPcm) {
      reportRenderProgress(jobId, { pct: 18, stage: "energy-curves" });
      const decoded = await decodeAudioToMonoPcm(audioFile, 22050);
      monoPcm = decoded.pcm;
      energy = computeEnergyCurves(monoPcm, 22050, 30);
    }

    const visualizerDescs: VisualizerWorkerDescriptor[] = [];
    if (spec.visualizers && monoPcm && energy) {
      for (const desc of spec.visualizers) {
        if (desc.type === "showwaves") {
          visualizerDescs.push({ type: "showwaves", pcm: monoPcm, sampleRate: 22050 });
        } else if (desc.type === "showfreqs") {
          visualizerDescs.push({ type: "showfreqs", energy });
        }
      }
    }

    // Master-loudness envelope for sidechain FX modulation — only when an
    // fx actually uses sidechain (reuse the mono decode if we already did
    // one for visualizers, else decode once at 22.05 kHz).
    let audioEnv: ReturnType<typeof buildLoudnessEnvelope> | null = null;
    if ((job.fx ?? []).some((f) => f.modulation?.timeMod === "sidechain")) {
      const pcm = monoPcm ?? (await decodeAudioToMonoPcm(audioFile, 22050)).pcm;
      audioEnv = buildLoudnessEnvelope(pcm, 22050);
    }

    reportRenderProgress(jobId, { pct: 25, stage: "encoding" });
    // Audio offset is anchored to cam-1 (the master clock cam), same sign
    // convention as the legacy single-cam pipeline.
    const totalOffsetMs = job.sync.offsetMs + (spec.offsetOverrideMs ?? 0);

    // Build the per-cam descriptors. Each cam's master-timeline start
    // position is derived from its own sync algorithm + the editor's
    // syncOverrideMs + startOffsetS; mirrors `clipRangeS()` on the editor
    // side so what the user previewed is what gets rendered.
    const overridesById = new Map(
      (spec.clipOverrides ?? []).map((o) => [o.id, o] as const),
    );
    const camInputs: CamWorkerInput[] = videos.map((v): CamWorkerInput => {
      const ov = overridesById.get(v.id);
      // User transform: prefer the latest from the editor's overrides
      // (covers in-flight tweaks before auto-persist debounces) and fall
      // back to whatever sits on the persisted asset row.
      const rotation = ov?.rotation ?? v.rotation ?? 0;
      const flipX = ov?.flipX ?? v.flipX ?? false;
      const flipY = ov?.flipY ?? v.flipY ?? false;
      const viewportTransform = ov?.viewportTransform;
      if (isImageAsset(v)) {
        // Image cams have no sync offset and no drift — masterStartS is
        // the user-set placement, sourceDurationS is the user-set length.
        const startOffsetS = ov?.startOffsetS ?? v.startOffsetS ?? 0;
        return {
          id: v.id,
          source: assetSource(v),
          masterStartS: startOffsetS,
          sourceDurationS: v.durationS,
          driftRatio: 1,
          kind: "image",
          rotation,
          flipX,
          flipY,
          viewportTransform,
        };
      }
      const algoMs = v.sync?.offsetMs ?? 0;
      const userMs = ov?.syncOverrideMs ?? 0;
      const startOffsetS = ov?.startOffsetS ?? 0;
      const masterStartS = -(algoMs + userMs) / 1000 + startOffsetS;
      // Per-clip trim: the cam still plays from source-time 0 onward
      // (masterStartS unchanged), but we narrow its "available" master-
      // timeline range to [trimInS, trimOutS]. activeCamAt + the
      // editor's clipRangeS use this same window to pick the active
      // cam, so cuts stay consistent between editor and render.
      const sourceDurationS = v.durationS ?? 0;
      const trimInS = Math.max(0, v.trimInS ?? 0);
      const trimOutS = Math.max(
        trimInS + 0.05,
        Math.min(sourceDurationS, v.trimOutS ?? sourceDurationS),
      );
      return {
        id: v.id,
        source: assetSource(v),
        masterStartS,
        sourceDurationS,
        driftRatio: v.sync?.driftRatio ?? 1,
        trimInS,
        trimOutS,
        rotation,
        flipX,
        flipY,
        viewportTransform,
      };
    });

    const cuts = spec.cuts ?? [];
    const masterDurationS = Math.max(
      ...camInputs.map((c) => c.masterStartS + c.sourceDurationS),
      0,
    );

    const workerInput: EditWorkerInput = {
      cams: camInputs,
      cuts,
      pills: spec.pills,
      masterDurationS,
      outputPath: outputPath(jobId),
      audioPcm: audio,
      segments: spec.segments,
      overlays: spec.overlays,
      visualizers: visualizerDescs,
      energy,
      fx: job.fx,
      bpm: job.bpm?.value ?? null,
      beatPhaseS: (job.bpm?.phase ?? 0) + (job.audioStartNudgeS ?? 0),
      beatsPerBar: job.beatsPerBar ?? 4,
      audioEnv,
      colorGrade: job.colorGrade as GradeParams | undefined,
      filterSlots: job.filterSlots,
      offsetMs: totalOffsetMs,
      driftRatio: job.sync.driftRatio,
      outputWidth: spec.exportOpts?.width,
      outputHeight: spec.exportOpts?.height,
      videoCodec: spec.exportOpts?.videoCodec,
      audioCodec: spec.exportOpts?.audioCodec,
      videoBitrateBps: spec.exportOpts?.videoBitrateBps,
      audioBitrateBps: spec.exportOpts?.audioBitrateBps,
    };

    // Collect transferables: every Float32Array buffer we hand to the
    // worker is detached on the main side, freeing memory immediately.
    const transferables: Transferable[] = [audio.pcm.buffer];
    for (const d of visualizerDescs) {
      if (d.type === "showwaves") transferables.push(d.pcm.buffer);
    }
    if (audioEnv && audioEnv.data instanceof Float32Array) {
      transferables.push(audioEnv.data.buffer);
    }

    const worker = new Worker(new URL("./render/edit.worker.ts", import.meta.url), {
      type: "module",
    });
    workerOwned = worker;
    activeRenders.set(jobId, worker);

    let lastDispatchedPct = 25;
    await new Promise<void>((resolve, reject) => {
      worker.addEventListener("message", (e: MessageEvent<EditWorkerEvent>) => {
        const msg = e.data;
        if (msg.type === "progress") {
          const p = msg.progress;
          // Map worker stages to UI percentages so the bar doesn't sit at
          // 89 % while the encoder flushes + the muxer writes 90 s of
          // frames to OPFS — a 2–10 s "silent" stretch on a typical
          // export that left the user wondering if anything was happening.
          if (p.stage === "video-encode" && p.framesTotal > 0) {
            const pct = 25 + Math.floor((p.framesDone / p.framesTotal) * 60);
            if (pct === lastDispatchedPct) return;
            lastDispatchedPct = pct;
            reportRenderProgress(jobId, {
              pct: Math.min(85, pct),
              stage: "encoding",
              framesDone: p.framesDone,
              framesTotal: p.framesTotal,
            });
          } else if (p.stage === "encoder-flush") {
            if (lastDispatchedPct === 87) return;
            lastDispatchedPct = 87;
            reportRenderProgress(jobId, { pct: 87, stage: "encoder-flush" });
          } else if (p.stage === "muxing" && p.framesTotal > 0) {
            // Streaming-mux: chunks-written / total-chunks scaled into 88-94 %.
            const pct = 88 + Math.floor((p.framesDone / p.framesTotal) * 6);
            if (pct === lastDispatchedPct) return;
            lastDispatchedPct = pct;
            reportRenderProgress(jobId, {
              pct: Math.min(94, pct),
              stage: "muxing",
              framesDone: p.framesDone,
              framesTotal: p.framesTotal,
            });
          } else if (p.stage === "muxing") {
            // Fallback for the initial muxing event before chunk progress.
            if (lastDispatchedPct === 88) return;
            lastDispatchedPct = 88;
            reportRenderProgress(jobId, { pct: 88, stage: "muxing" });
          } else if (p.stage === "finalizing") {
            if (lastDispatchedPct === 95) return;
            lastDispatchedPct = 95;
            reportRenderProgress(jobId, { pct: 95, stage: "finalizing" });
          } else if (p.stage === "audio-encode") {
            if (lastDispatchedPct === 18) return;
            lastDispatchedPct = 18;
            reportRenderProgress(jobId, { pct: 18, stage: "audio-encode" });
          }
        } else if (msg.type === "done") {
          resolve();
        } else if (msg.type === "error") {
          reject(new Error(msg.message));
        }
      });
      worker.addEventListener("error", (e) => {
        reject(new Error(e.message || "Render worker crashed"));
      });
      const startMsg: EditWorkerMessage = { type: "start", input: workerInput };
      worker.postMessage(startMsg, transferables);
    });

    reportRenderProgress(jobId, { pct: 95, stage: "writing" });
    const outputFile = await opfs.readFile(outputPath(jobId));
    const outputBytes = outputFile.size;

    const updated = await jobsDb.updateJob(jobId, {
      lastRender: {
        completedAt: Date.now(),
        outputBytes,
      },
      editSpec: spec,
    });
    emitJobUpdate(updated);
    useOpsStore.getState().finishRenderOp(jobId);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    useOpsStore.getState().failRenderOp(jobId, message);
    throw err;
  } finally {
    if (workerOwned) {
      workerOwned.terminate();
      activeRenders.delete(jobId);
    }
    removeRenderUnloadGuard(jobId);
  }
}

/**
 * Cancel an in-progress edit render. Hard-terminates the worker, deletes
 * the partial OPFS output, and flags the render op as cancelled so the
 * UI can distinguish it from a real failure.
 *
 * Safe to call when no render is active for the given job — it's a no-op.
 */
export async function cancelEditRender(jobId: string): Promise<void> {
  const worker = activeRenders.get(jobId);
  if (!worker) return;
  worker.terminate();
  activeRenders.delete(jobId);
  // Remove the half-written output file so a future render isn't fooled
  // by leftovers. Best-effort — the file may not exist yet.
  await opfs.deleteFile(outputPath(jobId)).catch(() => undefined);
  useOpsStore.getState().failRenderOp(jobId, "cancelled");
  removeRenderUnloadGuard(jobId);
}

/**
 * Resolve OPFS-backed object URLs for the editor / preview UI. Caller is
 * responsible for `URL.revokeObjectURL` when finished.
 *
 * For multi-cam jobs, `kind: "video"` and `kind: "frames"` always return
 * cam-1's asset (the legacy "the video" of a job). Multi-cam consumers
 * should resolve other cams via `resolveCamAssetUrl(jobId, camId, kind)`.
 */
export async function resolveJobAssetUrl(
  jobId: string,
  kind: "video" | "audio" | "output" | "frames",
): Promise<string | null> {
  if (kind === "output") {
    const exists = await opfs.exists(outputPath(jobId));
    if (!exists) return null;
    return opfs.objectUrl(outputPath(jobId));
  }
  const job = await jobsDb.getJob(jobId);
  if (!job) return null;

  if (kind === "frames") {
    const cam0 = job.videos?.[0];
    const cam0Frames =
      cam0 && isVideoAsset(cam0) ? cam0.framesPath : undefined;
    const path = cam0Frames ?? `jobs/${jobId}/frames.webp`;
    if (!(await opfs.exists(path))) return null;
    return opfs.objectUrl(path);
  }
  if (kind === "video") {
    const cam0 = job.videos?.[0];
    if (!cam0) return null;
    return objectUrlForAsset(cam0);
  }
  // audio
  return objectUrlForAssetSource(jobAudioSource(job));
}

/** Build a `blob:` URL for an asset's bytes — works for both
 *  OPFS-backed and handle-backed assets. Returns null when the
 *  underlying file is missing (legacy OPFS path) or when permission
 *  is denied (handle path). */
async function objectUrlForAsset(
  asset: { source?: AssetSource; opfsPath: string },
): Promise<string | null> {
  return objectUrlForAssetSource(assetSource(asset));
}

async function objectUrlForAssetSource(source: AssetSource): Promise<string | null> {
  if (source.kind === "opfs") {
    if (!(await opfs.exists(source.path))) return null;
    return opfs.objectUrl(source.path);
  }
  try {
    const file = await loadAssetFile({ source });
    return URL.createObjectURL(file);
  } catch {
    return null;
  }
}

/** Resolve the OPFS object URL for a specific cam's video or frames asset. */
export async function resolveCamAssetUrl(
  jobId: string,
  camId: string,
  kind: "video" | "frames",
): Promise<string | null> {
  const job = await jobsDb.getJob(jobId);
  if (!job?.videos) return null;
  const cam = job.videos.find((v) => v.id === camId);
  if (!cam) return null;
  // For "video" kind, return the asset's URL — works for both video
  // and image cams (browsers serve any blob URL to <video src> or
  // <img src> appropriately based on MIME) AND for both OPFS-backed
  // and FileSystemFileHandle-backed assets. For "frames", only video
  // assets have a thumbnail strip; image assets return null.
  if (kind === "video") {
    return objectUrlForAsset(cam);
  }
  // frames: always OPFS — we generated and own the strip
  if (!isVideoAsset(cam) || !cam.framesPath) return null;
  if (!(await opfs.exists(cam.framesPath))) return null;
  return opfs.objectUrl(cam.framesPath);
}

export async function deleteJob(jobId: string): Promise<void> {
  await opfs.deletePath(`jobs/${jobId}`);
  await jobsDb.deleteJob(jobId);
}

/**
 * Remove a single cam from an existing job. Cleans up the cam's video
 * file and its frame-strip in OPFS, prunes any cuts that referenced it,
 * and writes the slimmed videos[] back to IDB. Idempotent — unknown
 * camIds are a silent no-op.
 *
 * Note: cam ids stay stable for cams that *remain* (no renumbering).
 * That keeps existing cuts / overrides / persisted state valid.
 */
export async function removeCamFromJob(
  jobId: string,
  camId: string,
): Promise<void> {
  const job = await jobsDb.getJob(jobId);
  if (!job?.videos) return;
  const cam = job.videos.find((v) => v.id === camId);
  if (!cam) return;

  // Best-effort OPFS cleanup. Ignore not-found errors so a partially-
  // prepped cam (frames still missing) deletes cleanly. For
  // handle-backed assets the bytes live on the user's disk — we only
  // ever delete files we own (OPFS-backed copies + derived assets).
  if (cam.source) {
    try {
      await deleteAssetIfOwned(cam.source);
    } catch {
      // ignore
    }
  } else {
    try {
      await opfs.deletePath(cam.opfsPath);
    } catch {
      // ignore
    }
  }
  if (isVideoAsset(cam) && cam.framesPath) {
    try {
      await opfs.deletePath(cam.framesPath);
    } catch {
      // ignore
    }
  }

  const nextVideos = job.videos.filter((v) => v.id !== camId);
  const nextCuts = (job.cuts ?? []).filter((c) => c.camId !== camId);
  const updated = await jobsDb.updateJob(jobId, {
    videos: nextVideos,
    cuts: nextCuts,
  });
  emitJobUpdate(updated);
}

export { jobsDb };
export type { JobMode, LocalJob, SyncResult, Segment, TextOverlay };
